import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import _get from 'lodash/get';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { getErrors } from 'components/Survey/utils';
import { SURVEY_TYPES } from 'containers/Course/constants';
import AccountCircle from '@material-ui/icons/AccountCircle';
import { getUserFirstName, replacePlaceholders } from 'utils/stringUtils';
import { CourseCustomQuestions } from '../CourseCustomQuestions';

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiBackdrop-root': {
      backdropFilter: 'blur(4px)',
    },
  },
  button: {
    borderRadius: 8,
    textTransform: 'uppercase',
  },
  dialogTitle: {
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(80.57deg, #D2F4FD -23.74%, #DBE2F7 122.1%)',
    padding: theme.spacing(3),
  },
  dialogContent: {
    '& .MuiInputBase-root textarea': {
      fontSize: '1rem',
    },
  },
  dialogActions: {
    justifyContent: 'flex-start',
    padding: '0px 40px 28px',
  },
  closeButton: {
    marginLeft: 'auto',
    padding: 6,
  },
  avatarContainer: {
    marginRight: 8,
  },
  avatar: {
    height: props => (props.small ? 70 : 85),
    width: props => (props.small ? 70 : 85),
    borderRadius: '50%',
  },
  preTitleContainer: {
    display: 'flex',
  },
  preTitle: {
    fontWeight: '400',
  },
  postTitle: {
    textAlign: 'center',
    '& span': {
      fontSize: '2rem',
    },
  },
}));

export const CourseSurvey = ({
  type,
  course,
  onClose,
  coursesSiteCopy,
  small,
}) => {
  const classes = useStyles({ small });
  const [answers, setAnswers] = useState({});
  const firebase = useFirebase();
  const profile = useSelector(state => state.firebase.profile);

  const referral =
    type === SURVEY_TYPES.PRE
      ? _get(course.fields, 'preSurvey')
      : _get(course.fields, 'postSurvey');

  if (!referral) {
    return null;
  }

  const saveAnswer = ({ name, value }) => {
    setAnswers(prev => ({ ...prev, [name]: value }));
  };

  const onClick = () => {
    if (Object.keys(answers).length !== 0) {
      firebase.updateProfile({
        courseSurveys: {
          [type]: answers,
        },
      });
    }

    onClose();
  };

  const { avatarUrl } = profile;
  const name = getUserFirstName(profile);
  const title = _get(referral.fields, 'context.title', '');

  const labelsMap = {
    [SURVEY_TYPES.PRE]: {
      titleNode: (
        <div className={classes.preTitleContainer}>
          <div className={classes.avatarContainer}>
            {avatarUrl ? (
              <Avatar
                alt="User Avatar"
                src={avatarUrl}
                className={classes.avatar}
              />
            ) : (
              <AccountCircle className={classes.avatar} />
            )}
          </div>
          <div>
            <Typography variant={small ? 'h5' : 'h3'} color="textPrimary">
              {replacePlaceholders(title.split(',')[0], { '#name': name })}
            </Typography>
            <Typography
              className={classes.preTitle}
              color="textPrimary"
              variant={small ? 'h6' : 'h5'}
            >
              {title.split(',')[1]}
            </Typography>
          </div>
        </div>
      ),
      buttonLabel: _get(coursesSiteCopy, 'pageCopy.preSurveyButton'),
    },
    [SURVEY_TYPES.POST]: {
      titleNode: (
        <div className={classes.postTitle}>
          <span role="img" aria-label="Tada">
            🎉
          </span>
          <Typography variant={small ? 'h5' : 'h4'} color="textPrimary">
            {replacePlaceholders(title, { '#name': name })}
          </Typography>
        </div>
      ),
      buttonLabel: _get(coursesSiteCopy, 'pageCopy.postSurveyButton'),
    },
  };

  const currentLabels = labelsMap[type];

  const node = {
    data: {
      target: referral,
    },
  };

  const pages = [
    {
      elements: _get(referral.fields, 'copy.customAssessmentQuestion.data', []),
    },
  ];
  const errors = getErrors(pages, 0, answers);

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper, root: classes.root }}
      open
      onClose={onClose}
    >
      <div className={classes.dialogTitle}>
        <IconButton
          onClick={onClose}
          className={classes.closeButton}
          aria-label="Close Menu"
        >
          <CloseIcon />
        </IconButton>
        {currentLabels.titleNode}
      </div>
      <DialogContent className={classes.dialogContent}>
        <CourseCustomQuestions
          node={node}
          answers={answers}
          saveAnswer={saveAnswer}
        />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.button}
          onClick={onClick}
          variant="contained"
          color="primary"
          size="large"
          disabled={!!errors.length}
        >
          {currentLabels.buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
